'use client';

import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { QUERY_KEYS } from "@/common/types/react-query-keys.interfaces";
import { MembershipTypeProps } from "./MembershipType.interfaces";
import { Languages, UrlParamKeys } from "@/common/utils/constants";
import { useUpdateQueryParams } from "@/widgets/BecomeAMember/hooks/useUpdateQueryParams";

import { getClubById } from "@/common/utils/getClubById";
import { AnalyticsAdapterEventTypes } from "@/common/types/datalayer.interfaces";

export const MEMBERSHIP_TYPE_EXCLUDED_QUERY_KEYS: UrlParamKeys[] = [
    UrlParamKeys.AccessLevelName,
    UrlParamKeys.RateBundleId,
    UrlParamKeys.RateBundleTermId,
    UrlParamKeys.TermName,
    UrlParamKeys.PaidExtras,
    UrlParamKeys.FreeExtras,
    UrlParamKeys.ExtrasSave,
    UrlParamKeys.ExtraModules,
    UrlParamKeys.SkipPayment,
    UrlParamKeys.PaymentFrequency,
];

export const useMembershipType = ({
    data,
    lang,
    analytics,
    excludedQueryKeys,
    setIsPaymentWidgetVisible
}: Omit<MembershipTypeProps, 'step'>) => {
    const stableExcludedKeys = excludedQueryKeys ?? MEMBERSHIP_TYPE_EXCLUDED_QUERY_KEYS;

    const { updateUrl, searchParams } = useUpdateQueryParams(stableExcludedKeys);
    const [hasSetDefault, setHasSetDefault] = useState(false);

    const studioId = searchParams.get(UrlParamKeys.StudioId);
    const selectedType = searchParams.get(UrlParamKeys.Type);

    const defaultItem = useMemo(
        () => data?.find((item) => item.isDefaultCombination),
        [data]
    );

    const hasDefaultToBeSet = Boolean(defaultItem);

    const { data: clubData } = useQuery({
        queryKey: [QUERY_KEYS.CLUB_BY_ID, studioId, lang],
        queryFn: () =>
            getClubById({ clubId: Number(studioId), lang: lang as Languages }),
        enabled: Boolean(studioId),
    });

    useEffect(() => {
        if (hasSetDefault || selectedType || !data?.length) return;

        const itemToSelect =
            data.length === 1 ? data[0] : defaultItem;

        const typeName = itemToSelect?.title?.value;

        if (typeName) {
            updateUrl({ type: typeName });
            setHasSetDefault(true);
        }

    }, [hasSetDefault, selectedType, data, defaultItem]);

    const handleSelectType = useCallback(
        (typeName: string) => {
            updateUrl({ type: typeName });
            setIsPaymentWidgetVisible?.(false);
        },
        [updateUrl]
    );

    useEffect(() => {
        if (!clubData || !studioId) return;

        if (!hasDefaultToBeSet) {
            analytics.track({
                type: AnalyticsAdapterEventTypes.FUNNEL_START,
                payload: { clubData, studioId },
            });
            return;
        }

        if (!defaultItem) return;

        const membershipType = defaultItem.title?.value;
        const accessLevel = defaultItem.accessLevels.find(
            (level) => level.isDefaultCombination
        )?.title?.value;

        if (membershipType && accessLevel) {
            analytics.track({
                type: AnalyticsAdapterEventTypes.DEFAULT_MEMBERSHIP_SELECTED,
                payload: {
                    clubData,
                    studioId,
                    membershipType,
                    accessLevel,
                },
            });
        }
    }, [clubData, studioId, hasDefaultToBeSet, defaultItem]);

    return {
        selectedType,
        handleSelectType,
    }
}